import {
  CellProps,
  usePagination,
  useTable,
  Text,
  Badge,
  useForm,
  FiCheckCircle,
  FiAlertCircle,
  Flex,
  Button,
  FiRefreshCcw,
  useDisclosure,
} from '@agrotoken-tech/ui';
import { useDebounce, useTranslation } from '@bcba/react-hooks';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useMemo, useState } from 'react';
import { getSentDigitalDossierAccessRequests } from '../../../services/digital-dossier-access-requests/getSentDigitalDossierRequests';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { ExplorerListSentDigitalDossierAccessRequestsSerializer } from '@bcba/serializers';
import {
  viewerDigitalDossierGeneralStatusBadgeColors,
  accessRequestStatusBadgeColors,
} from '@bcba/frontend-utils';
import {
  DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS,
  DigitalDossierAccessRequestStatus,
  VIEWER_DIGITAL_DOSSIER_GENERAL_STATUS,
  digitalDossierAccessRequestStatuses,
} from '@bcba/types';
import { paths } from '../../../routes/paths';
import { useNavigate } from 'react-router-dom';

const digitalDossierStatusesIcon = {
  [VIEWER_DIGITAL_DOSSIER_GENERAL_STATUS.APPROVED]: FiCheckCircle,
  [VIEWER_DIGITAL_DOSSIER_GENERAL_STATUS.RUCA_EXPIRED]: FiAlertCircle,
  [VIEWER_DIGITAL_DOSSIER_GENERAL_STATUS.INFORMATION_EXPIRED]: FiAlertCircle,
};

export const useSentDigitalDossierAccessRequestsTable = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const formMethods = useForm({ defaultValues: { search: '' } });
  const { watch, setValue } = formMethods;
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedAccessRequestStatuses, setSelectedAccessRequestStatuses] =
    useState<DigitalDossierAccessRequestStatus[]>([]);
  const debouncedAccessRequestStatuses = useDebounce(
    selectedAccessRequestStatuses,
    300,
  );
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(
    null,
  );

  const {
    isOpen: isOpenRenewAccessRequestModal,
    onOpen: onOpenRenewAccessRequestModal,
    onClose: onCloseRenewAccessRequestModal,
  } = useDisclosure();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'sentDigitalDossierAccessRequests',
      page,
      debouncedSearch,
      debouncedAccessRequestStatuses,
    ],
    queryFn: () =>
      // TODO: pass debouncedSearch and debouncedAccessRequestStatuses as query params when implemented in backend
      getSentDigitalDossierAccessRequests({
        page,
      }),
    enabled:
      debouncedSearch !== undefined &&
      debouncedAccessRequestStatuses !== undefined,
  });

  const tableData = React.useMemo(
    () => data?.data || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  // todo: take highlights from this
  const highlightedStyles = useMemo(() => {
    const styles: any = {};
    tableData.forEach(
      ({ isHighlighted }: { isHighlighted: boolean }, index: number) => {
        if (isHighlighted) {
          styles[`tr:nth-of-type(${index + 1}) td`] = {
            bg: 'background-warning-default !important',
          };
        }
      },
    );
    return styles;
  }, [tableData]);

  const handleRenewAccessRequest = (companyUuid: string) => {
    setSelectedCompanyUuid(companyUuid);
    onOpenRenewAccessRequestModal();
  };

  const columns = useMemo(
    () => [
      {
        Header: t('accessRequests.table.requestedAt'),
        accessor: 'createdAt',
        Cell: (
          props: CellProps<ExplorerListSentDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { createdAt } = props.row.original;
          return (
            <Text size="sm">
              {createdAt
                ? formatDateToShortFormat(unixTimestampToDate(createdAt))
                : '-'}
            </Text>
          );
        },
      },
      {
        Header: t('accessRequests.table.companyFiscalId'),
        accessor: 'companyFiscalId',
      },
      {
        Header: t('accessRequests.table.companyName'),
        accessor: 'companyName',
      },
      {
        Header: t('accessRequests.table.requestStatus'),
        accessor: 'requestStatus',
        Cell: (
          props: CellProps<ExplorerListSentDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { requestStatus } = props.row.original;
          return (
            <Badge colorScheme={accessRequestStatusBadgeColors[requestStatus]}>
              {t(`accessRequests.status.${requestStatus}`)}
            </Badge>
          );
        },
      },
      {
        Header: t('accessRequests.table.dossierStatus'),
        accessor: 'digitalDossierStatuses',
        Cell: (
          props: CellProps<ExplorerListSentDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { digitalDossierStatus } = props.row.original;
          if (!digitalDossierStatus) return '-';
          return (
            <Flex direction="column" gap={0.5}>
              {digitalDossierStatus?.map((status) => {
                return (
                  <Badge
                    icon={digitalDossierStatusesIcon[status]}
                    iconPosition="leading"
                    colorScheme={
                      viewerDigitalDossierGeneralStatusBadgeColors[status]
                    }
                  >
                    {t(`accessRequests.status.digitalDossierStatus.${status}`)}
                  </Badge>
                );
              })}
            </Flex>
          );
        },
      },
      {
        Header: t('accessRequests.table.dossierUpdatedAt'),
        accessor: 'lastUpdated',
        Cell: (
          props: CellProps<ExplorerListSentDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { lastUpdated, isHighlighted } = props.row.original;
          return (
            <Text size="sm">
              {isHighlighted ? (
                <Text as="span" color="content-warning">
                  ●{'  '}
                </Text>
              ) : null}
              {lastUpdated
                ? formatDateToShortFormat(unixTimestampToDate(lastUpdated))
                : null}
            </Text>
          );
        },
      },
      {
        Header: t('common.actions'),
        accessor: 'actions',
        Cell: (
          props: CellProps<ExplorerListSentDigitalDossierAccessRequestsSerializer>,
        ) => {
          if (props.row.original.canRenewAccessRequest) {
            return (
              <Button
                leftIcon={<FiRefreshCcw />}
                variant="linkSecondary"
                size="sm"
                onClick={() => {
                  handleRenewAccessRequest(props.row.original.companyUuid);
                }}
              >
                {t('common.renewAccessRequest')}
              </Button>
            );
          } else {
            return <></>;
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableData],
  );

  const accessRequestStatusOptions = digitalDossierAccessRequestStatuses.map(
    (s) => ({
      text: t(`accessRequests.status.${s}`),
      value: s,
    }),
  );

  useEffect(
    () => setPage(1),
    [debouncedSearch, debouncedAccessRequestStatuses],
  );

  const onFiltersReset = () => {
    setValue('search', '');
    setSelectedAccessRequestStatuses([]);
  };

  const tableInstance = useTable(
    {
      columns: columns as any,
      data: tableData,
    },
    usePagination,
  );

  const isRowClickableCallback = (
    rowData: ExplorerListSentDigitalDossierAccessRequestsSerializer,
  ) => rowData.requestStatus === DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS.APPROVED;

  const handleRowClick = (
    rowData: ExplorerListSentDigitalDossierAccessRequestsSerializer,
  ) => {
    navigate(paths.sharedDigitalDossier.replace(':uuid', rowData.companyUuid));
  };

  return {
    data,
    formMethods,
    isLoading,
    onFiltersReset,
    setPage,
    tableInstance,
    accessRequestStatusOptions,
    selectedAccessRequestStatuses,
    setSelectedAccessRequestStatuses,
    isRowClickableCallback,
    handleRowClick,
    highlightedStyles,
    isOpenRenewAccessRequestModal,
    onOpenRenewAccessRequestModal,
    onCloseRenewAccessRequestModal,
    selectedCompanyUuid,
    setSelectedCompanyUuid,
    refetch,
  };
};
